import type {
  ExpenseByIdQuery,
  ExtraFieldFormInfoFragment,
} from '../../__generated__/graphql';
import type { ExpensesQuery } from '../../__generated__/graphql';
import {
  ExtraFieldsFormId,
  ExtraFieldContextParam,
} from '../../components/ExtraFields/types';
import {
  collectFormDataExtraFields,
  recordTypeFormKey,
} from '../../components/ExtraFields/logic';
import { ExtraFieldsVisibility } from '../../components/ExtraFields/extraFieldsVisibility';
import { ExpenseRecordType } from './types';
import {
  ExtraFieldInternalValue,
  InternalValue,
  mapToExtraFieldInternalValue,
} from '../../components/ExtraFields/extraFieldInternalValue';

type ExpenseFromApi =
  | NonNullable<ExpenseByIdQuery['expense']>
  | NonNullable<ExpensesQuery['me']>['myExpenses'][0];

type FormData = Record<string, any> & {
  description?: string | null;
  expenseDate?: Date | null;
  fromDate?: Date | null;
  categoryId?: string | null;
  category?: string | null;
  merchantId?: string | null;
  merchant?: string | null;
  [key: string]: any;
};

type ValidationOptions = {
  getFormFields: (formId: ExtraFieldsFormId) => ExtraFieldFormInfoFragment[];
  expenseData?: {
    expense?: {
      ContentDocumentLinks?: readonly any[] | null;
    } | null;
  } | null;
  validateInvoice?: boolean;
  contextParams?: ExtraFieldContextParam[];
  hasUploadedFile?: boolean;
};

const isExtraFieldValueValid = ({ value }: InternalValue): boolean => {
  return value !== null && value !== undefined && value.trim() !== '';
};

const validateExtraField = (
  field: ExtraFieldFormInfoFragment,
  extraFields?: readonly ExtraFieldInternalValue[] | null,
): boolean => {
  const extraFieldValue = extraFields?.find(
    (ef) => ef.fieldApiName === field.fieldApiName,
  );

  if (!extraFieldValue) return false;
  return isExtraFieldValueValid(extraFieldValue.internalValue);
};

const getRecordTypeName = (
  expenseOrFormData: ExpenseFromApi | FormData,
  isExpenseFromApi: boolean,
): string | null => {
  if (isExpenseFromApi) {
    const expense = expenseOrFormData as ExpenseFromApi;
    return (
      expense.recordType?.Name || expense.recordType?.DeveloperName || null
    );
  } else {
    const formData = expenseOrFormData as FormData;
    return formData[recordTypeFormKey] || null;
  }
};

const getVisibleRequiredExtraFields = (
  allFormFields: ExtraFieldFormInfoFragment[],
  recordTypeName: string | null,
  contextParams: ExtraFieldContextParam[],
): ExtraFieldFormInfoFragment[] => {
  const visibility = new ExtraFieldsVisibility();
  return allFormFields.filter((field) => {
    if (!field.required) return false;
    return visibility.isFieldVisible(field, {
      recordTypeName,
      contextParams,
    });
  });
};

const validateCoreFieldsFromApi = (
  expense: ExpenseFromApi,
): {
  isValid: boolean;
  hasDescription: boolean;
  hasMerchant: boolean;
  hasCategory: boolean;
  hasDate: boolean;
} => {
  const hasDescription =
    !!expense.flair__Description__c &&
    expense.flair__Description__c.trim() !== '';
  const hasMerchant = !!expense.merchant?.Id;
  const hasCategory = !!expense.expenseCategory?.Id;
  const hasDate = true;

  const recordTypeDeveloperName =
    expense.recordType?.DeveloperName || expense.recordType?.Name;
  const isReimbursement =
    recordTypeDeveloperName === ExpenseRecordType.Reimbursement;

  const isValid =
    hasDescription &&
    hasCategory &&
    hasDate &&
    (isReimbursement ? hasMerchant : true);

  return {
    isValid,
    hasDescription,
    hasMerchant,
    hasCategory,
    hasDate,
  };
};

const validateCoreFieldsFromFormData = (
  formData: FormData,
): {
  isValid: boolean;
  hasDescription: boolean;
  hasMerchant: boolean;
  hasCategory: boolean;
  hasDate: boolean;
} => {
  const hasDescription =
    !!formData.description && formData.description.trim() !== '';
  const hasDate = !!formData.expenseDate || !!formData.fromDate;
  const merchantIdValue = formData.merchantId ?? '';
  const merchantValue = formData.merchant ?? '';
  const hasMerchant =
    (typeof merchantIdValue === 'string' && merchantIdValue.trim() !== '') ||
    (typeof merchantValue === 'string' && merchantValue.trim() !== '');

  const categoryIdValue = formData.categoryId ?? '';
  const categoryValue = formData.category ?? '';
  const hasCategory =
    (typeof categoryIdValue === 'string' && categoryIdValue.trim() !== '') ||
    (typeof categoryValue === 'string' && categoryValue.trim() !== '');

  const recordTypeName = formData[recordTypeFormKey] || '';
  const isReimbursement =
    recordTypeName === ExpenseRecordType.Reimbursement ||
    recordTypeName?.includes(ExpenseRecordType.Reimbursement);

  const isValid =
    hasDescription &&
    hasCategory &&
    hasDate &&
    (isReimbursement ? hasMerchant : true);

  return {
    isValid,
    hasDescription,
    hasMerchant,
    hasCategory,
    hasDate,
  };
};

const validateInvoiceField = (
  expenseData: ValidationOptions['expenseData'],
  hasUploadedFile?: boolean,
): boolean => {
  const hasInvoiceInData = !!(
    expenseData?.expense?.ContentDocumentLinks &&
    expenseData.expense.ContentDocumentLinks.length > 0
  );
  return !!(hasUploadedFile || hasInvoiceInData);
};

const validateExtraFieldsFromApi = (
  visibleRequiredExtraFields: ExtraFieldFormInfoFragment[],
  expense: ExpenseFromApi,
): boolean => {
  return visibleRequiredExtraFields.every((field) =>
    validateExtraField(
      field,
      expense.extraFields.map(mapToExtraFieldInternalValue),
    ),
  );
};

const validateExtraFieldsFromFormData = (
  visibleRequiredExtraFields: ExtraFieldFormInfoFragment[],
  formData: FormData,
): boolean => {
  if (visibleRequiredExtraFields.length === 0) {
    return true;
  }

  const extraFields = collectFormDataExtraFields(formData);

  return visibleRequiredExtraFields.every((field) => {
    const extraFieldValue = extraFields.find(
      (ef) => ef.fieldApiName === field.fieldApiName,
    );

    if (!extraFieldValue) return false;
    return isExtraFieldValueValid(extraFieldValue.internalValue);
  });
};

export const validateExpense = (
  expenseOrFormData: ExpenseFromApi | FormData,
  options: ValidationOptions,
): boolean => {
  const {
    getFormFields,
    expenseData,
    validateInvoice = false,
    contextParams = [],
    hasUploadedFile = false,
  } = options;

  const isExpenseFromApi = 'Id' in expenseOrFormData;

  const coreFieldsValidation = isExpenseFromApi
    ? validateCoreFieldsFromApi(expenseOrFormData as ExpenseFromApi)
    : validateCoreFieldsFromFormData(expenseOrFormData as FormData);

  const hasInvoice = validateInvoice
    ? validateInvoiceField(expenseData, hasUploadedFile)
    : true;

  const recordTypeName = getRecordTypeName(expenseOrFormData, isExpenseFromApi);
  const allFormFields = getFormFields(ExtraFieldsFormId.ExpensesDetails);
  const visibleRequiredExtraFields = getVisibleRequiredExtraFields(
    allFormFields,
    recordTypeName,
    contextParams,
  );

  const hasAllRequiredExtraFields =
    visibleRequiredExtraFields.length === 0 ||
    (isExpenseFromApi
      ? validateExtraFieldsFromApi(
          visibleRequiredExtraFields,
          expenseOrFormData as ExpenseFromApi,
        )
      : validateExtraFieldsFromFormData(
          visibleRequiredExtraFields,
          expenseOrFormData as FormData,
        ));

  return !!(
    coreFieldsValidation.isValid &&
    hasInvoice &&
    hasAllRequiredExtraFields
  );
};
